<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white px-[10%] py-12 max-sm:px-5 max-sm:py-8">
    <!-- Hero Section -->
    <div class="flex flex-col lg:flex-row gap-12 items-center mb-16">
      <div class="relative group flex-1">
        <img 
          :src="require('@/assets/about-img.png')" 
          class="w-full max-w-2xl rounded-2xl shadow-2xl border-2 border-gray-700 group-hover:border-cyan-400 transition-all duration-500"
          alt="Hédi Ben Chiboub"
        >
        <div class="absolute -inset-4 border-2 border-gray-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
      </div>

      <div class="flex-1 space-y-6">
        <h1 class="text-5xl max-sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
          Hédi Ben Chiboub
        </h1>
        <h2 class="text-2xl text-gray-300 font-medium">
          Full Stack Developer & AI Enthusiast
        </h2>
        
        <div class="flex flex-wrap gap-3 mt-4">
          <span v-for="(tech, index) in technologies" :key="index" 
            class="px-3 py-1 bg-gray-800/50 text-cyan-300 rounded-full text-sm border border-gray-700">
            {{ tech }}
          </span>
        </div>

        <p class="text-lg text-gray-300 leading-relaxed">
          Passionate about creating elegant solutions to complex problems. 
          I blend technical expertise with creative thinking to build 
          impactful digital experiences.
        </p>

        <div class="flex gap-4 pt-2">
          <a v-for="(social, index) in socialLinks" :key="index" 
            :href="social.link" target="_blank"
            class="p-2 bg-gray-800/50 rounded-lg hover:bg-cyan-400/10 hover:text-cyan-400 transition-all border border-gray-700 hover:border-cyan-400/30"
            :aria-label="social.name">
            <Icon :icon="social.icon" width="1.5em" height="1.5em" />
          </a>
        </div>
      </div>
    </div>

    <!-- Animated Content Sections -->
    <div class="space-y-12 max-w-4xl mx-auto">
      <!-- About Section -->
      <section class="fade-in-section" v-for="(section, index) in contentSections" :key="index" 
        :ref="el => sectionRefs[index] = el">
        <h3 class="text-2xl font-bold mb-4 text-cyan-400 flex items-center gap-2">
          <Icon :icon="section.icon" />
          {{ section.title }}
        </h3>
        
        <div 
          class="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-6 shadow-lg markdown-content"
          v-html="renderMarkdown(section.markdown)"
        ></div>
      </section>

      <!-- CV Download -->
      <div class="fade-in-section " ref="cvSection">
        <div class="cv-bg mb-12 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-400/20 rounded-xl p-6 text-center">
          <h3 class="text-2xl font-bold mb-3 text-cyan-400">Want my full professional details?</h3>
          <p class="text-gray-300 mb-6">Download my comprehensive CV for all my skills, experience, and education history.</p>
          <button @click="downloadCV" 
            class="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg font-medium flex items-center gap-2 mx-auto hover:shadow-lg hover:shadow-cyan-500/20 transition-all">
            <Icon icon="bi:filetype-pdf" />
            <span>Download CV</span>
            <Icon icon="solar:download-linear" class="animate-bounce" />
          </button>
        </div>
      </div>
    </div>
    
    
  </div>
</template>

<script setup>
import { Icon } from "@iconify/vue";
import { ref, onMounted } from 'vue';

// Simple markdown renderer
const renderMarkdown = (text) => {
  // Bold text
  let html = text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-cyan-400">$1</strong>');
  
  // Italic text
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Lists
  html = html.replace(/^- (.*$)/gm, '<li class="list-disc ml-5 mb-1">$1</li>');
  
  // Blockquotes
  html = html.replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-cyan-400 pl-4 italic text-gray-400 my-4">$1</blockquote>');
  
  // Line breaks
  html = html.replace(/\n\n/g, '<br><br>');
  
  // Paragraphs
  html = html.replace(/(<li>.*<\/li>)/gs, '<ul class="mb-4">$1</ul>');
  
  return html;
};

// Content Data
const technologies = ref([
  'JavaScript', 'Vue.js', 'React', 'Node.js',
  'Python', 'Django', 'C++', 'UE5',
  'Blender3d', 'Premier Pro', 'Photoshop', 'Arduino',
  'After Effects', 'React Native', 'Tailwindcss', 'Python again' 
]);

const socialLinks = ref([
  { name: 'YouTube', icon: 'logos:youtube-icon', link: 'https://www.youtube.com/@Chiboub-v9c' },
  { name: 'SoundCloud', icon: 'logos:soundcloud', link: 'https://soundcloud.com/chibbers7' },
  { name: 'Twitter', icon: 'pajamas:twitter', link: 'https://x.com/OnePm36' },
  { name: 'Email', icon: 'logos:google-gmail', link: 'mailto:benchiboub.hedi@gmail.com' },
  { name: 'Stack Overflow', icon: 'logos:stackoverflow-icon', link: 'https://stackoverflow.com/users/9022257/h%c3%a9di-ben-chiboub' },
  { name: 'LeetCode', icon: 'arcticons:leetcode', link: 'https://leetcode.com/u/RqmrsDfGPe/' }
]);

const contentSections = ref([
  {
    title: 'My Journey',
    icon: 'fluent-emoji-high-contrast:rocket',
    markdown: `
      My coding journey began when I was **15 years old**, tinkering with HTML and CSS trying to replicate Facebook. 
      Since then, I've evolved into a full-stack developer with expertise in both frontend and backend technologies.
        <br><br>
      - 🎓 **Self-taught** through online resources and hands-on projects<br>
      - 💻 **2+ years** of professional development experience<br>
      - 🧠 Particularly passionate about **AI/ML** and its applications<br>
      - 🌍 Fluent in **Arabic, French, and English**
    `
  },
  {
    title: 'Thought',
    icon: 'fluent-emoji-high-contrast:thinking-face',
    markdown: `
      I believe in **clean, efficient code** that solves real problems. My development principles:
        <br><br>
      
      - 🛠️ **Practical solutions** over theoretical perfection<br>
      - 📈 **Continuous learning** - the tech world never stops evolving<br>
      - 🤝 **Open communication** with clients and team members<br>
      - ♻️ **Sustainable code** that's maintainable and scalable
        <br><br>
      
      > "First solve the problem. Then write the code." - John Johnson
    `
  },
  {
    title: 'Current Focus',
    icon: 'fluent-emoji-high-contrast:light-bulb',
      markdown: (`
        Currently diving deep into:
        <br><br>
        - 🤖 **Generative AI** and LLM applications<br>
        - 🏗️ **Microservices architecture** at scale<br>
        - 🧪 **Technical writing** to share knowledge<br>
        <br>
        Always working on **side projects** to explore new technologies and push my boundaries.
      `)
    }
]);

// Animation Logic
const sectionRefs = ref([]);
const cvSection = ref(null);

const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('animate-fadeIn');
    }
  });
}, {
  threshold: 0.1
});

onMounted(() => {
  sectionRefs.value.forEach(section => {
    if (section) observer.observe(section);
  });
  if (cvSection.value) observer.observe(cvSection.value);
});

// CV Download
const downloadCV = () => {
  console.log('Downloading CV...');
  alert('CV download would start here in a production environment');
};
</script>

<style>
.fade-in-section {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.fade-in-section.animate-fadeIn {
  opacity: 1;
  transform: translateY(0);
}

/* Markdown content styling */
.markdown-content {
  @apply text-gray-300 leading-relaxed break-words whitespace-normal overflow-hidden;

  /* Additional styling for markdown elements */
  ul {
    @apply list-disc ml-5 mb-4;
  }
  
  strong {
    @apply text-cyan-400;
  }
  
  blockquote {
    @apply border-l-4 border-cyan-400 pl-4 italic text-gray-400 my-4;
  }
  
  /* For code blocks if needed */
  pre {
    @apply bg-gray-900/50 p-4 rounded overflow-x-auto;
  }
  
  code {
    @apply text-cyan-300;
  }
}
</style>